<template>
  <div class="initiatives-head text-medium text-primary">
    <div class="row-wrapper d-inline-flex min-w-100">
      <div class="row no-gutters flex-nowrap min-w-100">
        <div class="cell title-cell col pb-1 pointer">
          <sort-dropdown
            title="Initiative"
            column-key="title"
            align="left"
            :show-sort-options="showSortOptions"
            :sort-options="sortOptions.title"
            :sorted-direction="sortedDirection"
            :sorted-by="sortedBy"
            :show-filter-options="showFilterOptions"
            :disabled="!showSortOptions"
            @sort="onSort"
            @reset="$emit('reset')"
          />
        </div>
        <div
          class="cell text-center pb-1 flex-shrink-0"
          :class="{ pointer: getSortOptions(field) }"
          v-for="field in sortedFields"
          :key="field.id"
          :style="{ width: `${field.width}px` }"
        >
          <sort-dropdown
            :title="field.title"
            :align="field.align"
            :column-key="getFieldName(field)"
            :sort-options="getSortOptions(field)"
            :sorted-direction="sortedDirection"
            :sorted-by="sortedBy"
            :filter-options="filterOptions[getFieldName(field)]"
            :filtered-by="filteredBy && filteredBy[getFieldName(field)]"
            :show-filter-options="showFilterOptions"
            :show-sort-options="showSortOptions"
            :show-field-settings="showFieldSettingsInColumn"
            :field-setting-options="getFieldSettingOptions(field)"
            :disabled="!showSortOptions && !showFieldSettingsInColumn"
            @sort="onSort"
            @reset="$emit('reset')"
            @filter="onFilter({ filterParam: getFieldName(field), ...$event })"
            @field-setting="onFieldSetting(field, $event.id)"
          />
        </div>
        <div class="overflow-plc flex-center" title="Add Field">
          <span class="add-cf pointer" v-if="!hideAddCustomFieldIcon" :id="'add-cf-' + uuid">
            <i class="plus-icon"></i>
          </span>
        </div>
        <initiative-fields-menu
          :display-fields="displayFields"
          :auto-save-column-settings="autoSaveColumnSettings"
          :org-id="orgId"
          :team-id="teamId"
          :project-id="projectId"
          :target="'add-cf-' + uuid"
          @enable-cfs="$emit('enable-cfs', $event)"
          v-if="!hideAddCustomFieldIcon"
        />
        <remove-custom-field-confirm-dialog
          ref="removeDialog"
          :team-id="teamId"
          :project-id="projectId"
          :org-id="orgId"
          :custom-field="currentCustomField"
          @removed="refreshCFs"
          v-if="teamId"
        />
        <custom-field-editor-modal
          :custom-field="currentCustomField"
          v-model="isEditFieldModalShown"
          :org-id="orgId"
          :team-id="teamId"
          :project-id="projectId"
          @deleted="onDeleteCF"
          v-if="isEditFieldModalShown"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import CustomFieldEditorModal from '@/components/initiative/CustomFieldEditorModal.vue'
import InitiativeFieldsMenu from '@/components/initiative/InitiativeFieldsMenu.vue'
import RemoveCustomFieldConfirmDialog from '@/components/initiative/RemoveCustomFieldConfirmDialog.vue'
import SortDropdown from '@/components/SortDropdown.vue'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import {
  ALPHABETICAL_SORT_OPTIONS,
  COMPUTED_FIELD_SORT_OPTIONS,
  DATE_SORT_OPTIONS,
  FILTER_OPTIONS,
  INITIATIVE_MANDATORY_FIELD,
  INITIATIVE_STANDARD_FIELD_NAME,
  INITIATIVE_STANDARD_FIELDS,
  InitiativeFieldType
} from '@/constants/initiative'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import getTeamSpaceName from '@/utils/getTeamSpaceName'
import showAPIError from '@/utils/showAPIError'
import sortInitiativeFields from '@/utils/sortInitiativeFields'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This is the header component for initiative table
 * @emit
 * - sort: this is emitted when header of any column is clicked
 */
export default {
  name: 'InitiativesHeader',
  props: {
    sortedBy: {},
    sortedDirection: {},
    filteredBy: { default: () => ({}) },
    showFilterOptions: { default: true },
    showSortOptions: { default: true },
    showFieldSettingsInColumn: Boolean,
    teamId: Number,
    orgId: Number,
    projectId: Number,
    /**
     * These are the columns to be shown in table
     */
    fieldsToDisplay: {
      type: Array,
      default: () => INITIATIVE_STANDARD_FIELDS.filter(x => x.name !== INITIATIVE_MANDATORY_FIELD)
    },
    hideAddCustomFieldIcon: Boolean,
    autoSaveColumnSettings: Boolean
  },
  components: {
    CustomFieldEditorModal,
    RemoveCustomFieldConfirmDialog,
    InitiativeFieldsMenu,
    SortDropdown
  },
  mixins: [ComponentIdMixin],
  data() {
    return {
      sortOptions: {
        title: ALPHABETICAL_SORT_OPTIONS,
        priority: [
          { title: 'High → Low', direction: 'descending' },
          { title: 'Low → High', direction: 'ascending' }
        ],
        due_date: DATE_SORT_OPTIONS,
        owners: ALPHABETICAL_SORT_OPTIONS,
        status: ALPHABETICAL_SORT_OPTIONS,
        cf: ALPHABETICAL_SORT_OPTIONS,
        ...COMPUTED_FIELD_SORT_OPTIONS
      },
      filterOptions: FILTER_OPTIONS,
      currentCustomField: null,
      isEditFieldModalShown: false
    }
  },
  computed: {
    sortedFields() {
      return sortInitiativeFields(this.fieldsToDisplay)
    },
    displayFields() {
      if (!this.autoSaveColumnSettings) {
        const [cf, sf] = R.partition(
          field => field.fieldType === InitiativeFieldType.CUSTOM_FIELD,
          this.fieldsToDisplay
        )
        return {
          customFieldDefinitionIds: cf.map(x => x.id),
          standardFieldNames: sf.map(x => x.name)
        }
      }
      return null
    }
  },
  methods: {
    ...mapActions(['getDisplayInitiativeFields', 'removeDisplayInitiativeField']),
    getFieldSettingOptions(field) {
      const options = []
      const isCF = field.fieldType === InitiativeFieldType.CUSTOM_FIELD
      if (isCF) {
        options.push({
          id: 'edit',
          title: 'Edit Field'
        })
      }
      if (!this.hideAddCustomFieldIcon) {
        options.push({
          id: 'hide',
          title: 'Hide Field'
        })
      }
      if (isCF && this.autoSaveColumnSettings) {
        // autoSaveColumnSettings is true only when changes have to be persisted in BE. Same condition should be applied for "Remove from Team/Project"
        options.push({
          id: 'remove',
          title: `Remove from ${
            this.projectId ? 'Project' : getTeamSpaceName(this.$store.getters.teamById(this.teamId))
          }`
        })
      }
      return options
    },
    getFieldName(field) {
      if (field.id) {
        return `cf_${field.id}`
      } else {
        return field.name
      }
    },
    getSortOptions(field) {
      if (field.id) {
        return this.sortOptions.cf
      } else {
        return this.sortOptions[field.name]
      }
    },
    sendEvent(field, addEvent) {
      switch (field) {
        case INITIATIVE_STANDARD_FIELD_NAME.TITLE:
          addEvent(EVENT_NAME.TITLE)
          break
        case INITIATIVE_STANDARD_FIELD_NAME.STATUS:
          addEvent(EVENT_NAME.STATUS)
          break
        case INITIATIVE_STANDARD_FIELD_NAME.DUE_DATE:
          addEvent(EVENT_NAME.DUE)
          break
        case INITIATIVE_STANDARD_FIELD_NAME.PRIORITY:
          addEvent(EVENT_NAME.PRIORITY)
          break
        case INITIATIVE_STANDARD_FIELD_NAME.OWNERS:
          addEvent(EVENT_NAME.OWNER)
          break
      }
    },
    addFilterAnalytics({ filterParam }) {
      const addEvent = R.curryN(3, trackEvent)(
        EVENT_CATEGORY.PREFERENCE,
        EVENT_ACTION.FILTER_INITIATIVES
      )
      this.sendEvent(filterParam, addEvent)
    },
    addSortAnalytics({ by }) {
      const addEvent = R.curryN(3, trackEvent)(
        EVENT_CATEGORY.PREFERENCE,
        EVENT_ACTION.SORT_INITIATIVES
      )
      this.sendEvent(by, addEvent)
    },
    onSort(sortParams) {
      this.$emit('sort', sortParams)
      this.addSortAnalytics(sortParams)
    },
    onFilter(payload) {
      this.$emit('filter', payload)
      this.addFilterAnalytics(payload)
    },
    onFieldSetting(field, action) {
      switch (action) {
        case 'edit':
          this.currentCustomField = field
          this.isEditFieldModalShown = true
          break
        case 'hide':
          this.hideField(field)
          break
        case 'remove':
          this.currentCustomField = field
          this.$refs.removeDialog.show()
      }
    },
    async hideField(field) {
      const isCF = field.fieldType === InitiativeFieldType.CUSTOM_FIELD
      if (!this.autoSaveColumnSettings) {
        let displayFields = this.displayFields
        if (isCF) {
          displayFields = {
            ...displayFields,
            customFieldDefinitionIds: displayFields.customFieldDefinitionIds.filter(
              fieldId => fieldId !== field.id
            )
          }
        } else {
          displayFields = {
            ...displayFields,
            standardFieldNames: displayFields.standardFieldNames.filter(
              fieldId => fieldId !== field.name
            )
          }
        }
        this.$emit('enable-cfs', displayFields)
      } else {
        try {
          const payload = {}
          if (isCF) {
            payload.customFieldDefinitionId = field.id
          } else {
            payload.standardFieldName = field.name
          }
          await this.removeDisplayInitiativeField({
            orgId: this.orgId,
            teamId: this.teamId,
            projectId: this.projectId,
            displayFieldId: field.displayFieldId
          })
          this.refreshDisplayFields()
        } catch (e) {
          showAPIError(e)
        }
      }
    },
    refreshDisplayFields() {
      this.getDisplayInitiativeFields({
        orgId: this.orgId,
        teamId: this.teamId,
        projectId: this.projectId
      })
    },
    refreshCFs() {
      this.refreshDisplayFields()
      this.currentCustomField = null
    },
    onDeleteCF() {
      if (this.autoSaveColumnSettings) {
        this.refreshCFs()
      } else {
        const displayFields = {
          ...this.displayFields,
          customFieldDefinitionIds: this.displayFields.customFieldDefinitionIds.filter(
            fieldId => fieldId !== this.currentCustomField.id
          )
        }
        this.$emit('enable-cfs', displayFields)
        this.currentCustomField = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import './styles/rowCellStyles';

.initiatives-head {
  user-select: none;
}

.add-cf {
  padding: 4px;
  display: inline-flex;
  border-radius: 4px;
  background-color: $quaternary;
  transition: background-color 100ms ease-in;

  &:hover {
    background-color: $tertiary;
  }
}
</style>
